import React, { useCallback, useEffect, useRef, useState } from "react";
import OftadehLayout from "../../components/OftadehLayout/OftadehLayout";
import { Alert } from "@material-ui/lab";
import { observer } from "mobx-react-lite";
import { useIsMounted } from "../../helpers/useIsMounted";
import { useStore } from "../../stores/helpers/useStore";
import RegularsTable from "./RegularsTable";

const RegularsList = () => {
	const isMounted = useIsMounted();
	const [selectedRoom, setSelectedRoom] = useState("");
	const [emailFilter, setEmailFilter] = useState("");
	const skipEmailDebounce = useRef(true);

	const {
		regularsStore: {
			entries,
			rooms,
			total,
			page,
			limit,
			getRegularsList,
		},
		uiStore: { loading },
	} = useStore();

	const fetchRegulars = useCallback(
		({ roomCode = selectedRoom, email = emailFilter, page: nextPage = page, limit: nextLimit = limit } = {}) =>
			getRegularsList({
				roomCode: roomCode || null,
				email: email || null,
				page: nextPage,
				limit: nextLimit,
			}),
		[emailFilter, getRegularsList, limit, page, selectedRoom]
	);

	useEffect(() => {
		getRegularsList({ page: 1, limit: 50 });
	}, [getRegularsList]);

	useEffect(() => {
		if (skipEmailDebounce.current) {
			skipEmailDebounce.current = false;
			return;
		}

		const timer = setTimeout(() => {
			fetchRegulars({ page: 1 });
		}, 400);

		return () => clearTimeout(timer);
	}, [emailFilter, fetchRegulars]);

	const handleRoomChange = (roomCode) => {
		setSelectedRoom(roomCode);
		fetchRegulars({ roomCode, page: 1 });
	};

	const handlePageChange = (nextPage) => {
		fetchRegulars({ page: nextPage });
	};

	const handleLimitChange = (nextLimit) => {
		fetchRegulars({ page: 1, limit: nextLimit });
	};

	const handleExportCsv = async () => {
		const response = await getRegularsList({
			roomCode: selectedRoom || null,
			email: emailFilter || null,
			page: 1,
			limit: 5000,
			exportAll: true,
		});

		return response.entries || [];
	};

	return (
		<OftadehLayout loading={loading || !isMounted}>
			<div style={{ marginBottom: "20px" }}>
				<h1>Regulars List</h1>
				<p style={{ marginTop: "8px", color: "#666" }}>
					Players who opted in to receive updates from your hosts. Filter by
					room code when you manage multiple hosts.
				</p>
			</div>

			<RegularsTable
				entries={entries}
				rooms={rooms}
				total={total}
				page={page}
				limit={limit}
				selectedRoom={selectedRoom}
				emailFilter={emailFilter}
				onRoomChange={handleRoomChange}
				onEmailChange={setEmailFilter}
				onRefresh={() => fetchRegulars({ page })}
				onPageChange={handlePageChange}
				onLimitChange={handleLimitChange}
				onExportCsv={handleExportCsv}
			/>

			{!entries.length && !loading && (
				<Alert severity="info" style={{ marginTop: "16px" }}>
					No regulars yet. Once hosts enable the regulars list and players
					submit their email, entries will appear here.
				</Alert>
			)}
		</OftadehLayout>
	);
};

export default observer(RegularsList);
